import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { ChevronLeft, UserPlus, UserMinus, MessageCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import ProfileHeader from "../components/ProfileHeader";
import ReputationCardCompact from "../components/ReputationCardCompact";
import ProfileTabs from "../components/ProfileTabs";
import { toast } from "sonner";

export default function UserProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: viewer } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/users/${userId}`);
      setProfile(data);
    } finally { setLoading(false); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // Check follow state (only for signed-in visitors on someone else)
  useEffect(() => {
    if (!viewer || !profile || viewer.user_id === profile.user_id) return;
    (async () => {
      try {
        const { data } = await api.get("/users/me/following");
        setFollowing((data || []).includes(profile.user_id));
      } catch { /* ignore */ }
    })();
  }, [viewer, profile]);

  const toggleFollow = async () => {
    if (!viewer) { navigate("/login"); return; }
    setFollowBusy(true);
    try {
      const { data } = await api.post(`/users/${profile.user_id}/follow`);
      setFollowing(!!data.following);
      setProfile((p) => p ? { ...p, followers_count: (p.followers_count || 0) + (data.following ? 1 : -1) } : p);
      toast.success(data.following ? "Followed" : "Unfollowed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setFollowBusy(false); }
  };

  if (loading || !profile) {
    return (
      <div className="min-h-[60dvh] grid place-items-center">
        <div className="w-10 h-10 rounded-full border-2 border-pink-500/40 border-t-pink-500 animate-spin" />
      </div>
    );
  }

  // If viewing your own /u/:id URL, redirect to /profile for the full owner surface.
  if (viewer && viewer.user_id === profile.user_id) {
    navigate("/profile", { replace: true });
    return null;
  }

  // If the profile belongs to a vendor, keep sending users to the richer
  // storefront view — that's the intended vendor surface.
  if (profile.is_vendor) {
    navigate(`/vendor/${profile.user_id}`, { replace: true });
    return null;
  }

  const isSelf = viewer && viewer.user_id === profile.user_id;
  const actions = (
    <>
      {!isSelf && (
        <Button
          onClick={toggleFollow}
          disabled={followBusy}
          className={`rounded-full h-10 px-5 text-xs font-black ${
            following
              ? "bg-white/5 border border-white/10 text-zinc-200 hover:bg-white/10"
              : "bg-pink-500 hover:bg-pink-400 text-white glow-pink"
          }`}
          data-testid="user-follow-btn"
        >
          {following
            ? (<><UserMinus className="w-3.5 h-3.5 mr-1.5" /> Following</>)
            : (<><UserPlus className="w-3.5 h-3.5 mr-1.5" /> Follow</>)}
        </Button>
      )}
      {!isSelf && viewer && (
        <Button
          onClick={() => navigate(`/messages/new?to=${profile.user_id}`)}
          className="rounded-full h-10 px-4 bg-white/5 hover:bg-white/10 border border-white/10 text-white text-xs font-bold"
          data-testid="user-message-btn"
        >
          <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Message
        </Button>
      )}
    </>
  );

  return (
    <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4 space-y-4 pb-24" data-testid="user-profile-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white"
        data-testid="user-back-btn"
      >
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <ProfileHeader profile={profile} isOwner={false} actions={actions} />

      {/* Public reputation card */}
      <ReputationCardCompact userId={profile.user_id} />

      {/* Tabs — Saved hidden for visitors */}
      <ProfileTabs userId={profile.user_id} isOwner={false} />
    </div>
  );
}
